import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { NEW_MEMBER_MCP_TOOLS, canSeeInventory } from '@infras/shared-types';
import { Icon } from '../components/Icon';
import { McpConnectSteps } from '../components/McpConnectSteps';
import { McpTokens } from '../components/McpTokens';
import { ACCESS_REQUEST_FIELDS, statusBadgeClass, type AccessRequest } from '../lib/access';
import { useMe } from '../lib/useMe';

/**
 * Onboarding for every role, and the permanent home of a `new_member`.
 *
 * It used to read as documentation — numbered prose that said the same thing to
 * everyone whether or not they had already done it. It is now a CHECKLIST against
 * the viewer's real state, so the page says what is LEFT rather than what exists.
 *
 * Every mark is read from data, never asserted: token count from `mcpTokens`, role
 * from the server-verified `me`, open requests from `myAccessRequests`. A checklist
 * that guessed would be worse than the prose it replaced.
 */
const STATE = gql`
  ${ACCESS_REQUEST_FIELDS}
  query GetStartedState {
    mcpTokens {
      id
      revoked
      expiresAt
      lastUsedAt
    }
    myAccessRequests {
      ...AccessRequestFields
    }
  }
`;

interface TokenLite {
  id: string;
  revoked: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
}

/** One checklist row. `done` drives the mark, so it has to come from data. */
function Step({
  n,
  title,
  done,
  status,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  status?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className={`step${done ? ' step--done' : ''}`}>
      <div className="step__head">
        <span className={`step__mark${done ? ' step__mark--done' : ''}`} aria-hidden="true">
          {done ? <Icon name="check" /> : n}
        </span>
        <h3 className="step__title">{title}</h3>
        {status && <span className="step__status">{status}</span>}
      </div>
      {children && <div className="step__body">{children}</div>}
    </section>
  );
}

export function GetStartedPage() {
  const { me } = useMe();
  const { data } = useQuery<{ mcpTokens: TokenLite[]; myAccessRequests: AccessRequest[] }>(STATE, {
    fetchPolicy: 'cache-and-network',
  });

  const isNewMember = me?.role === 'new_member';
  const hasInventory = me ? canSeeInventory(me.role) : false;

  // "Live" = usable right now. Counting raw rows would tick step 1 for someone
  // whose only token expired last month or was revoked.
  const now = Date.now();
  const liveTokens = (data?.mcpTokens ?? []).filter(
    (t) => !t.revoked && (!t.expiresAt || new Date(t.expiresAt).getTime() > now),
  );
  const usedTokens = liveTokens.filter((t) => t.lastUsedAt);
  const openRequests = (data?.myAccessRequests ?? []).filter((r) => r.status === 'pending');

  return (
    <div className="page stack">
      <div className="detail-head">
        <h1>Get started</h1>
        {me && <span className="badge">{me.role.replace('_', ' ')}</span>}
      </div>

      <p className="detail-desc">
        OptiSigns infrastructure inventory — repos, clusters, workloads, secrets (names only) and
        deploy catalogs. It is <strong>agent-first</strong>: the primary interface is a hosted MCP
        server, and the web pages are for manual research.
      </p>

      <Step
        n={1}
        title="Mint a personal access token"
        done={liveTokens.length > 0}
        status={
          liveTokens.length > 0
            ? `${liveTokens.length} live token${liveTokens.length === 1 ? '' : 's'}`
            : 'none yet'
        }
      >
        <McpTokens />
      </Step>

      <Step
        n={2}
        title="Connect your AI agent"
        // A token that has never been used means the agent was never actually
        // wired up. That's the honest signal, and we already store it.
        done={usedTokens.length > 0}
        status={usedTokens.length > 0 ? 'an agent has used your token' : 'no token used yet'}
      >
        <McpConnectSteps tokenPanelInline />
      </Step>

      <Step
        n={3}
        title="Get a role assigned"
        done={hasInventory}
        status={
          hasInventory
            ? `you are ${me?.role.replace('_', ' ')}`
            : openRequests.length > 0
              ? `${openRequests.length} request${openRequests.length === 1 ? '' : 's'} pending`
              : 'new member — inventory hidden'
        }
      >
        {hasInventory ? (
          <p className="detail-desc">
            Your role opens the inventory pages and the full MCP tool set. Secret visibility follows
            it:{' '}
            {me?.allowedLevels?.length ? (
              me.allowedLevels.map((l) => (
                <span key={l} className="tag-badge" style={{ marginRight: 4 }}>
                  {l}
                </span>
              ))
            ) : (
              <span className="muted">none</span>
            )}
          </p>
        ) : (
          <>
            <p className="detail-desc">
              With no role you can read the docs, mint a token, and use{' '}
              {NEW_MEMBER_MCP_TOOLS.map((t, i) => (
                <span key={t}>
                  {i > 0 && ', '}
                  <code>{t}</code>
                </span>
              ))}
              . The inventory pages stay hidden until a <code>root</code> user assigns your role.
            </p>
            {/* Straight to the form. The old copy said "ask in DevOps" — the Slack
                DM this whole feature exists to replace. */}
            <Link to="/access/new" className="btn btn-primary">
              Request a role
            </Link>
          </>
        )}

        {openRequests.length > 0 && (
          <ul className="waiting-list" style={{ marginTop: 10 }}>
            {openRequests.map((r) => (
              <li className="waiting-line" key={r.id}>
                <Link to={`/access/${r.id}`}>#{r.id}</Link>
                <span className="waiting-line__what">{r.resourceName}</span>
                <span className={statusBadgeClass(r.status)}>{r.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Step>

      {/* No mark: knowing where things are isn't a task you complete. */}
      <Step n={4} title="Know where to look" done={false}>
        <p className="detail-desc">
          Tool reference: <Link to="/docs/mcp">MCP tools</Link>. How access requests work:{' '}
          <Link to="/docs/request-access">Requesting access</Link>.
          {hasInventory && (
            <>
              {' '}
              Manual research: <Link to="/">Overview</Link>, <Link to="/repos">Repos</Link>,{' '}
              <Link to="/clusters">Clusters</Link>, <Link to="/secrets">Secrets</Link>,{' '}
              <Link to="/catalog">Deploy catalog</Link>.
            </>
          )}
        </p>
        {isNewMember && (
          <p className="muted text-2xs">
            This page stays your home until a role is assigned — nothing else is hidden from you by
            accident.
          </p>
        )}
      </Step>
    </div>
  );
}
